import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { MODEL_PROVIDERS, MODELS, getModelDisplayName } from '../../constants/models';
import { 
  getModelConfig, 
  getDefaultLLMConfig, 
  isParamSupported, 
  getParamLimits,
  LLM_PROVIDERS,
  isReasoningModel 
} from '../../constants/llmConfig';

const AnthropicIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M14.5 2L22 22h-4L14 14 10 22H6l7.5-20h1z" fill="currentColor"/>
    <path d="M9.5 2L2 22h4l4-8 4 8h4L10.5 2h-1z" fill="currentColor" opacity="0.6"/>
  </svg>
);

const OpenAIIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22.282 9.821a5.985 5.985 0 00-.516-4.91 6.046 6.046 0 00-6.51-2.9A6.065 6.065 0 004.981 4.18a5.985 5.985 0 00-3.998 2.9 6.046 6.046 0 00.743 7.097 5.98 5.98 0 00.51 4.911 6.051 6.051 0 006.515 2.9A5.985 5.985 0 0013.26 24a6.056 6.056 0 005.772-4.206 5.99 5.99 0 003.997-2.9 6.056 6.056 0 00-.747-7.073zM13.26 22.43a4.476 4.476 0 01-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 00.392-.681v-6.737l2.02 1.168a.071.071 0 01.038.052v5.583a4.504 4.504 0 01-4.494 4.494zM3.6 18.304a4.47 4.47 0 01-.535-3.014l.142.085 4.783 2.759a.771.771 0 00.78 0l5.843-3.369v2.332a.08.08 0 01-.033.062L9.74 19.95a4.5 4.5 0 01-6.14-1.646zM2.34 7.896a4.485 4.485 0 012.366-1.973V11.6a.766.766 0 00.388.676l5.815 3.355-2.02 1.168a.076.076 0 01-.071 0l-4.83-2.786A4.504 4.504 0 012.34 7.896zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 01.071 0l4.83 2.791a4.494 4.494 0 01-.676 8.105v-5.678a.79.79 0 00-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 00-.785 0L9.409 9.23V6.897a.066.066 0 01.028-.061l4.83-2.787a4.5 4.5 0 016.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 01-.038-.057V6.075a4.5 4.5 0 017.375-3.453l-.142.08L8.704 5.46a.795.795 0 00-.393.681v6.733zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5v-3z" fill="currentColor"/>
  </svg>
);

const GroqIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="4" y="4" width="7" height="7" rx="1" fill="currentColor"/>
    <rect x="13" y="4" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
    <rect x="4" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.7"/>
    <rect x="13" y="13" width="7" height="7" rx="1" fill="currentColor" opacity="0.5"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m7.08 7.08l4.24 4.24M1 12h6m6 0h6M4.22 19.78l4.24-4.24m7.08-7.08l4.24-4.24"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12"/>
  </svg>
);

const getProviderIcon = (provider) => {
  if (provider === LLM_PROVIDERS.ANTHROPIC) return <AnthropicIcon />;
  if (provider === LLM_PROVIDERS.OPENAI) return <OpenAIIcon />;
  if (provider === LLM_PROVIDERS.GROQ) return <GroqIcon />;
  return null;
};

const getProviderKey = (provider) => {
  if (provider === MODEL_PROVIDERS.OPENAI || provider === LLM_PROVIDERS.OPENAI) return LLM_PROVIDERS.OPENAI;
  if (provider === MODEL_PROVIDERS.ANTHROPIC || provider === LLM_PROVIDERS.ANTHROPIC) return LLM_PROVIDERS.ANTHROPIC;
  if (provider === MODEL_PROVIDERS.GROQ || provider === LLM_PROVIDERS.GROQ) return LLM_PROVIDERS.GROQ;
  return LLM_PROVIDERS.ANTHROPIC;
};

const isProviderAvailable = (provider, availableProviders) => {
  if (!availableProviders) return true; // If not specified, all are available
  const providerKey = provider.toLowerCase();
  return availableProviders[providerKey] !== false;
};

const ModelSelector = ({ 
  model, 
  setModel, 
  llmConfig, 
  setLLMConfig,
  models = MODELS,
  availableProviders = null // { openai: true, anthropic: true, groq: true }
}) => {
  const navigate = useNavigate();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [tempModel, setTempModel] = useState(model);
  const [tempProvider, setTempProvider] = useState(() => {
    const currentModel = models.find(m => m.value === model);
    return currentModel ? getProviderKey(currentModel.provider) : LLM_PROVIDERS.ANTHROPIC;
  });
  const [tempConfig, setTempConfig] = useState({});
  
  const modalRef = useRef(null);
  const modelDropdownRef = useRef(null);

  const currentModel = models.find(m => m.value === model);
  const currentProvider = currentModel ? getProviderKey(currentModel.provider) : LLM_PROVIDERS.ANTHROPIC;

  // Auto-switch to available provider if current provider is not available
  useEffect(() => {
    if (!availableProviders) return; // No restrictions
    
    const currentModelProvider = currentModel ? getProviderKey(currentModel.provider) : null;
    if (currentModelProvider && !isProviderAvailable(currentModelProvider, availableProviders)) {
      // Find an available provider
      const availableProvider = Object.values(LLM_PROVIDERS).find(p => 
        isProviderAvailable(p, availableProviders)
      );
      
      if (availableProvider) {
        // Find a model from the available provider
        const providerModels = models.filter(m => getProviderKey(m.provider) === availableProvider);
        if (providerModels.length > 0) {
          const recommendedModel = providerModels.find(m => m.recommended) || providerModels[0];
          setModel(recommendedModel.value);
          if (setLLMConfig) {
            setLLMConfig(getDefaultLLMConfig(recommendedModel.value));
          }
        }
      }
    }
  }, [availableProviders, currentModel, models, setModel, setLLMConfig]);

  useEffect(() => {
    if (showConfigModal) {
      setTempModel(model);
      const currentModel = models.find(m => m.value === model);
      setTempProvider(currentModel ? getProviderKey(currentModel.provider) : LLM_PROVIDERS.ANTHROPIC);
      setTempConfig(llmConfig || getDefaultLLMConfig(model));
    }
  }, [showConfigModal, model, llmConfig, models]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(e.target)) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenModal = () => {
    setShowConfigModal(true);
  };

  const handleCloseModal = () => {
    setShowConfigModal(false);
  };

  const handleProviderChange = (provider) => {
    setTempProvider(provider);
    const providerModels = models.filter(m => getProviderKey(m.provider) === provider);
    if (providerModels.length > 0) {
      const recommended = providerModels.find(m => m.recommended) || providerModels[0];
      setTempModel(recommended.value);
      setTempConfig(getDefaultLLMConfig(recommended.value));
    }
  };

  const handleModelChange = (modelValue) => {
    setTempModel(modelValue);
    setTempConfig(getDefaultLLMConfig(modelValue));
    setIsModelDropdownOpen(false);
  };

  const handleConfigChange = (param, value) => {
    setTempConfig(prev => ({ ...prev, [param]: value }));
  };

  const handleResetToDefaults = () => {
    setTempConfig(getDefaultLLMConfig(tempModel));
  };

  const handleSave = () => {
    setModel(tempModel);
    if (setLLMConfig) {
      setLLMConfig({ ...tempConfig, model: tempModel });
    }
    setShowConfigModal(false);
  };

  const renderParamInput = (param, label, type = 'number', step = 0.1) => {
    if (!isParamSupported(tempModel, param)) return null;
    
    const limits = getParamLimits(tempModel, param);
    const value = tempConfig[param] ?? '';

    // Check if it's a select/enum type
    if (limits && limits.options && Array.isArray(limits.options)) {
      return (
        <div className="flex flex-col gap-2" key={param}>
          <label className="text-sm font-medium text-text-primary">{label}</label>
          <select
            className="w-full py-3 px-3 text-base rounded-md cursor-pointer transition-all duration-fast focus:outline-none bg-surface-secondary/60 border border-primary-500/15 text-text-primary"
            value={value}
            onChange={(e) => handleConfigChange(param, e.target.value)}
          >
            <option value="">Select...</option>
            {limits.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    const percentage = ((value - (limits?.min || 0)) / ((limits?.max || 1) - (limits?.min || 0))) * 100;

    return (
      <div className="flex flex-col gap-3" key={param}>
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-medium text-text-primary">{label}</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '-') {
                  handleConfigChange(param, '');
                } else {
                  const numVal = type === 'number' ? parseFloat(val) : val;
                  if (!isNaN(numVal)) {
                    const clampedVal = Math.min(Math.max(numVal, limits?.min || 0), limits?.max || Infinity);
                    handleConfigChange(param, clampedVal);
                  }
                }
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value);
                if (isNaN(val) || val < (limits?.min || 0)) {
                  handleConfigChange(param, limits?.min || 0);
                } else if (val > (limits?.max || Infinity)) {
                  handleConfigChange(param, limits?.max);
                }
              }}
              placeholder={limits && limits.min !== undefined ? `${limits.min}-${limits.max}` : ''}
              className="w-24 py-1.5 px-2.5 text-sm font-medium text-center rounded-md transition-all duration-fast focus:outline-none bg-surface-secondary/60 border border-primary-500/15 text-text-primary"
            />
            {limits && limits.min !== undefined && (
              <span className="text-xs whitespace-nowrap text-text-tertiary">{limits.min} - {limits.max}</span>
            )}
          </div>
        </div>
        
        <div className="relative w-full">
          <input
            type="range"
            value={value || limits?.min || 0}
            onChange={(e) => handleConfigChange(param, type === 'number' ? parseFloat(e.target.value) : e.target.value)}
            step={step}
            min={limits?.min}
            max={limits?.max}
            className="w-full h-2 bg-transparent rounded-lg appearance-none cursor-pointer model-selector-range"
            style={{
              background: `linear-gradient(to right, #32CD32 0%, #32CD32 ${percentage}%, #161C16 ${percentage}%, #161C16 100%)`
            }}
          />
        </div>
      </div>
    );
  };

  const filteredModels = models.filter(m => getProviderKey(m.provider) === tempProvider);
  const tempModelDetails = models.find(m => m.value === tempModel);
  const tempModelConfig = getModelConfig(tempModel);

  // Check if any provider is available
  const hasAnyProviderAvailable = !availableProviders || 
    Object.values(LLM_PROVIDERS).some(p => isProviderAvailable(p, availableProviders));
  
  // Check if current model's provider is available
  const isCurrentProviderAvailable = isProviderAvailable(currentProvider, availableProviders);

  return (
    <div className="w-full">
      {/* Warning when no providers are configured */}
      {availableProviders && !hasAnyProviderAvailable && (
        <div className="mb-3 p-3 bg-warning-bg border border-warning/30 rounded-lg">
          <div className="flex items-center justify-between gap-2 text-sm text-warning-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>No API keys configured.</span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/workspace-settings', { state: { tab: 'apikeys' } })}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-fast bg-warning/20 hover:bg-warning/30 text-warning-400 hover:text-warning-300 border border-warning/30 hover:border-warning/50"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"/>
              </svg>
              Configure API Keys
            </button>
          </div>
        </div>
      )}
      
      {/* Warning when current provider is not available */}
      {availableProviders && hasAnyProviderAvailable && !isCurrentProviderAvailable && (
        <div className="mb-3 p-2 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center gap-2 text-xs text-primary-600">
            <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            <span>Switching to available provider...</span>
          </div>
        </div>
      )}
      
      <div className={`flex items-center justify-between gap-3 py-3 px-4 bg-bg-secondary border rounded-lg transition-all duration-fast hover:border-border-hover hover:bg-bg-hover ${
        availableProviders && !isCurrentProviderAvailable ? 'border-error/50' : 'border-border-primary'
      }`}>
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-xs font-medium text-text-tertiary uppercase tracking-wider">
            <div className="relative">
              {getProviderIcon(currentProvider)}
              {availableProviders && !isCurrentProviderAvailable && (
                <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-error text-white text-[8px] flex items-center justify-center font-bold" title="API key not configured">
                  !
                </span>
              )}
            </div>
            <span>{currentProvider}</span>
            {availableProviders && !isCurrentProviderAvailable && (
              <span className="text-[10px] font-medium text-error normal-case tracking-normal">(No API key)</span>
            )}
          </div>
          <div className="text-sm font-semibold text-text-primary leading-tight">
            {currentModel?.label || getModelDisplayName(model) || model || 'No model selected'}
          </div>
          {currentModel?.description && (
            <div className="text-xs text-text-secondary leading-tight">
              {currentModel.description}
            </div>
          )}
        </div>
        
        <button
          type="button"
          className="flex items-center justify-center w-9 h-9 p-0 bg-bg-tertiary border border-border-primary rounded-md text-text-secondary cursor-pointer transition-all duration-fast flex-shrink-0 hover:bg-primary-50 hover:border-primary-500 hover:text-primary-600 [&:hover_svg]:rotate-90 [&_svg]:transition-transform [&_svg]:duration-200"
          onClick={handleOpenModal}
          title="Configure model and parameters"
        >
          <SettingsIcon />
        </button>
      </div>

      {showConfigModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-[1000] animate-fadeIn model-config-overlay" 
          onClick={handleCloseModal}
        >
          <div 
            className="w-[90%] max-w-[700px] max-h-[90vh] rounded-xl overflow-hidden flex flex-col animate-slideUp sm:w-[95%] sm:max-h-[95vh] model-config-modal" 
            ref={modalRef} 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between py-5 px-6 flex-shrink-0 border-b border-primary-500/15 model-config-header">
              <h3 className="m-0 text-xl font-semibold text-text-primary">Model Configuration</h3>
              <button 
                type="button" 
                className="flex items-center justify-center w-8 h-8 p-0 rounded-md cursor-pointer transition-all duration-fast bg-transparent border border-transparent text-text-tertiary hover:bg-success-50 hover:text-primary-500"
                onClick={handleCloseModal}
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="flex-1 py-6 px-6 overflow-y-auto min-h-0 flex flex-col gap-5">
              {/* Provider Selection */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Provider</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {Object.values(LLM_PROVIDERS).map((provider) => {
                    const IconComponent = provider === LLM_PROVIDERS.ANTHROPIC ? AnthropicIcon :
                                         provider === LLM_PROVIDERS.OPENAI ? OpenAIIcon : GroqIcon;
                    const isSelected = tempProvider === provider;
                    const isAvailable = isProviderAvailable(provider, availableProviders);
                    return (
                      <button
                        key={provider}
                        type="button"
                        disabled={!isAvailable}
                        className={`flex flex-row items-center justify-center gap-2 py-3 px-3 text-sm font-medium rounded-lg transition-all duration-fast relative ${
                          isAvailable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                        } ${
                          isSelected 
                            ? 'bg-success-50 border-2 border-primary-500 text-primary-500 shadow-glow-sm' 
                            : 'bg-surface-secondary/60 border-2 border-primary-500/15 text-text-secondary hover:bg-success-50/50 hover:border-primary-500/30'
                        }`}
                        onClick={() => isAvailable && handleProviderChange(provider)}
                        title={!isAvailable ? `${provider} API key not configured` : ''}
                      >
                        <span className={`flex items-center justify-center text-base transition-all duration-fast ${isSelected ? '' : 'grayscale-[40%]'}`}>
                          <IconComponent />
                        </span>
                        <span className="text-sm">{provider}</span>
                        {!isAvailable && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-error/80 text-white text-xs flex items-center justify-center" title="API key not configured">
                            !
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Model Selection */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Model</label>
                <div className="relative w-full" ref={modelDropdownRef}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-3 py-3 px-3 rounded-md cursor-pointer transition-all duration-fast text-left bg-surface-secondary/60 ${
                      isModelDropdownOpen 
                        ? 'border border-primary-500 ring-4 ring-primary-500/10' 
                        : 'border border-primary-500/15'
                    }`}
                    onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                  >
                    <span className="flex items-center justify-center flex-shrink-0 w-5 h-5 text-primary-500">
                      {getProviderIcon(tempProvider)}
                    </span>
                    <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-medium text-text-primary">{tempModelDetails?.label || tempModel}</span>
                      <span className="text-xs leading-tight whitespace-nowrap overflow-hidden text-ellipsis text-text-tertiary">{tempModelDetails?.description}</span>
                    </div>
                    <svg className={`flex-shrink-0 transition-transform duration-200 text-text-tertiary ${isModelDropdownOpen ? 'rotate-180' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  {isModelDropdownOpen && (
                    <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[100] max-h-80 overflow-y-auto rounded-lg animate-slideUp model-dropdown">
                      {filteredModels.map((m) => {
                        const isModelSelected = tempModel === m.value;
                        return (
                          <button
                            key={m.value}
                            type="button"
                            className={`w-full flex items-center gap-3 py-3 px-3 border-none cursor-pointer transition-all duration-fast text-left border-b border-primary-500/10 last:border-b-0 ${
                              isModelSelected ? 'bg-success-50' : 'bg-transparent hover:bg-success-50/50'
                            }`}
                            onClick={() => handleModelChange(m.value)}
                          >
                            <span className={`flex items-center justify-center flex-shrink-0 w-5 h-5 ${isModelSelected ? 'text-primary-500' : 'text-text-tertiary'}`}>
                              {getProviderIcon(tempProvider)}
                            </span>
                            <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                              <span className="text-sm font-medium text-text-primary">{m.label}</span>
                              <span className="text-xs leading-tight text-text-tertiary">{m.description}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-text-tertiary">
                  <span>Context: {tempModelConfig?.contextWindow?.toLocaleString() || 'N/A'} tokens</span>
                  <span>•</span>
                  <span>Max Output: {tempModelConfig?.maxOutput?.toLocaleString() || 'N/A'} tokens</span>
                </div>
              </div>

              {/* Parameters */}
              <div className="flex flex-col gap-3">
                <label className="text-sm font-semibold uppercase tracking-wider text-text-secondary">Parameters</label>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
                  {renderParamInput('max_tokens', 'Max Tokens', 'number', 1)}
                  {!isReasoningModel(tempModel) && renderParamInput('temperature', 'Temperature', 'number', 0.1)}
                  {!isReasoningModel(tempModel) && renderParamInput('top_p', 'Top P', 'number', 0.01)}
                  {!isReasoningModel(tempModel) && renderParamInput('frequency_penalty', 'Frequency Penalty', 'number', 0.1)}
                  {!isReasoningModel(tempModel) && renderParamInput('presence_penalty', 'Presence Penalty', 'number', 0.1)}
                  {!isReasoningModel(tempModel) && renderParamInput('seed', 'Seed', 'number', 1)}
                  {isReasoningModel(tempModel) && renderParamInput('reasoning_effort', 'Reasoning Effort', 'text', 0.1)}
                </div>
              </div>

              {/* Not Supported Parameters */}
              {tempModelConfig?.not_supports && tempModelConfig.not_supports.length > 0 && (
                <div className="flex items-start gap-2 py-3 px-3 rounded-md text-sm leading-normal bg-info-50 border border-info/20 text-info">
                  <svg className="flex-shrink-0 mt-0.5 text-info" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Not supported: {tempModelConfig.not_supports.join(', ')}</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 py-4 px-6 flex-shrink-0 border-t border-primary-500/15 model-config-footer">
              <button 
                type="button" 
                className="py-3 px-5 text-sm font-medium min-w-[120px] rounded-md cursor-pointer transition-all duration-fast bg-surface-secondary/80 text-text-secondary border border-primary-500/20 hover:bg-success-50 hover:border-primary-500/40"
                onClick={handleResetToDefaults}
              >
                Reset to Defaults
              </button>
              <button 
                type="button" 
                className="py-3 px-5 text-sm font-medium min-w-[120px] rounded-md cursor-pointer transition-all duration-fast bg-gradient-to-br from-primary-600 to-primary-500 text-black border-none shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
                onClick={handleSave}
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .model-config-overlay {
          background: rgba(3, 6, 4, 0.7);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .model-config-modal {
          background: rgba(14, 19, 14, 0.95);
          border: 1px solid rgba(50, 205, 50, 0.15);
          box-shadow: 0 25px 50px rgba(3, 6, 4, 0.5), 0 0 40px rgba(50, 205, 50, 0.1);
        }
        .model-config-header {
          background: linear-gradient(180deg, rgba(50, 205, 50, 0.05) 0%, transparent 100%);
        }
        .model-config-footer {
          background: rgba(10, 15, 10, 0.8);
        }
        .model-dropdown {
          background: rgba(14, 19, 14, 0.98);
          border: 1px solid rgba(50, 205, 50, 0.2);
          box-shadow: 0 10px 30px rgba(3, 6, 4, 0.5);
        }
        .model-selector-range::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #32CD32;
          cursor: pointer;
          border: 3px solid #030604;
          box-shadow: 0 0 10px rgba(50, 205, 50, 0.4);
          transition: all 0.2s ease;
        }
        .model-selector-range::-webkit-slider-thumb:hover {
          background: #66BB6A;
          transform: scale(1.1);
          box-shadow: 0 0 15px rgba(50, 205, 50, 0.6);
        }
        .model-selector-range::-webkit-slider-thumb:active {
          transform: scale(1.05);
          box-shadow: 0 0 8px rgba(50, 205, 50, 0.3);
        }
        .model-selector-range::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #32CD32;
          cursor: pointer;
          border: 3px solid #030604;
          box-shadow: 0 0 10px rgba(50, 205, 50, 0.4);
          transition: all 0.2s ease;
        }
        .model-selector-range::-moz-range-thumb:hover {
          background: #66BB6A;
          transform: scale(1.1);
          box-shadow: 0 0 15px rgba(50, 205, 50, 0.6);
        }
        .model-selector-range::-moz-range-thumb:active {
          transform: scale(1.05);
          box-shadow: 0 0 8px rgba(50, 205, 50, 0.3);
        }
      `}</style>
    </div>
  );
};

ModelSelector.propTypes = {
  model: PropTypes.string.isRequired,
  setModel: PropTypes.func.isRequired,
  llmConfig: PropTypes.object,
  setLLMConfig: PropTypes.func,
  models: PropTypes.array,
  availableProviders: PropTypes.shape({
    openai: PropTypes.bool,
    anthropic: PropTypes.bool,
    groq: PropTypes.bool,
  }),
};

ModelSelector.defaultProps = {
  llmConfig: null,
  setLLMConfig: null,
  models: MODELS,
  availableProviders: null,
};

export default ModelSelector;
